import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import { Model } from '@/models/Model';
// import { AxiosError } from 'axios';
import debounce from 'debounce';
import { isArray } from 'lodash';

@Component<MiDataTable>({})
export default class MiDataTable extends Vue {
  protected get perPage(): number {
    if (this.$limit) { return this.$limit; }
    if (this.options && this.options.limit) { return this.options.limit; }
    return 25;
  }

  protected get headers(): DataTableHeader[] {
    if (! this.options || ! this.options.headers) {
      return [];
    }

    this.options.headers.forEach((header: DataTableHeader) => {
      if (header.value === 'actions') {
        header.sortable = false;
      }
    });

    return this.options.headers;
  }

  @Prop()
  protected options!: MiDataTableOptions;

  protected tableOptions: MiDataTableOptions = {
    model: new Model(),
    title: '',
    headers: [],
    limit: 25,
    itemKey: 'id', // wordt primary key model
    loadingText: 'Loading',
    noDataText: 'Geen data beschikbaar',
    noResultsText: 'Geen resultaten gevonden',
    compact: false,
    singleSelect: false,
    verticalFilter: true,
  };

  // eslint-disable-next-line
  @Prop({ default: () => { } })
  protected visibility!: MiDataTableVisibility;

  protected visible: MiDataTableVisibility = {
    pagination: true,
    header: true,
    columnHeaders: true,
    footer: true,
    select: false,
    search: true,
    filters: false,
  };

  protected isLoading = false;

  protected isShowingFilters = false;

  protected filtersHeight = 0;

  protected selected = [];

  protected total = 0;

  protected page = 1;

  protected lastPage = 1;

  protected $limit: number | null = null;

  protected search = '';

  protected searchDebounce: () => void = this.handleSearch();

  protected items: Model[] = [];

  @Prop({ default: '' })
  protected sortBy!: string;

  protected sortDesc = false;

  public initialize(): void {
    this.visible = { ...{}, ...this.visible, ...this.visibility };
    this.tableOptions = { ...{}, ...this.tableOptions, ...this.options };
    this.page = 1;
    this.fetchSource();
  }

  public handleSearch(): () => void {
    return debounce(() => {
      this.page = 1;
      this.fetchSource();
    }, 400);
  }

  public refresh(): void {
    this.fetchSource();
  }

  protected mounted(): void {
    if (! this.options || ! this.options.lazy) {
      this.initialize();
    }
  }

  protected fetchSource(): void {
    if (! this.options || ! this.options.model) { return; }

    if (this.sortBy) {
      this.options.model.sort(this.sortBy, this.sortDesc ? 'DESC' : 'ASC');
    } else {
      this.options.model.clearSort();
    }

    this.isLoading = true;
    this.options.model
      .limit(this.perPage)
      .filter('search', this.search)
      .page(this.page)
      .all()
      .then((items: Model[]) => {
        this.items = items;
        this.total = items[0] ? (items[0].meta as TableMeta).total as number : 0;
        this.lastPage = items[0] ? (items[0].meta as TableMeta).last_page as number : 1;
        this.isLoading = false;
      });
    // FIXME: Implement ErrorHandler;
    // .catch((error: AxiosError) => {
    //   alert(error);
    // });
  }

  protected handlePageChange(page: number): void {
    this.page = page;
    this.fetchSource();
  }

  protected handleSortBy(value: string | string[]): void {
    if (isArray(value) && value.length) {
      [this.sortBy] = value;
      return;
    }
    this.sortBy = '';
  }

  protected handleSortDesc(sortDesc: boolean | boolean[]): void {
    if (isArray(sortDesc) && sortDesc.length) {
      [this.sortDesc] = sortDesc;
    } else {
      this.sortDesc = false;
    }

    this.page = 1;
    this.fetchSource();
  }

  protected handlePerPageChange(perPage: number): void {
    // FIXME: Needs implementation
    this.isLoading = true;
    this.$limit = perPage;
    this.fetchSource();
  }

  protected handleFilterClick(event: MouseEvent): void {
    if (! this.tableOptions) { return; }

    if (this.tableOptions.verticalFilter) {
      this.animateVerticalFilterBar();
    } else {
      this.animateHorizontalFilterBar(event);
    }
  }

  protected animateVerticalFilterBar(): void {
    this.isShowingFilters = ! this.isShowingFilters;
  }

  protected animateHorizontalFilterBar(event: MouseEvent): void {
    const datatableContainer = (event.target as HTMLElement).closest('.mi-datatable-container');
    if (! datatableContainer) { return; }

    if (! this.isShowingFilters) {
      this.isShowingFilters = true;
      const innerFilters = datatableContainer.getElementsByClassName('mi-data-table-inner-filters');
      this.filtersHeight = innerFilters[0].clientHeight;
    } else {
      this.isShowingFilters = false;
      this.filtersHeight = 0;
    }
  }

  protected showVerticalFilter() {
    if (! this.isShowingFilters) {
      this.isShowingFilters = true;
    }
  }

  protected toggleVerticalFilterPeek(event: any) {
    //
  }

  protected get dataTableWidth(): string {
    if (! this.tableOptions || ! this.tableOptions.verticalFilter) {
      return 'w-full';
    }

    return this.isShowingFilters ? 'w-3/4' : 'w-full';
  }

  protected get verticalFilterWidth(): string {
    if (! this.tableOptions || ! this.tableOptions.verticalFilter) {
      return 'w-filter';
    }

    return this.isShowingFilters ? 'w-1/4' : 'w-filter';
  }

  @Watch('selected')
  protected selectedChanged(): void {
    this.$emit('input', this.selected);
  }

  @Watch('options')
  protected optionsChanged(): void {
    this.initialize();
  }
}

export interface MiDataTableVisibility {
  pagination?: boolean;
  header?: boolean;
  columnHeaders?: boolean;
  footer?: boolean;
  select?: boolean;
  search?: boolean;
  filters?: boolean;
}

export interface MiDataTableOptions {
  model: Model;
  headers: DataTableHeader[];
  title?: string;
  limit?: DataTableLimit;
  itemKey?: string;
  loadingText?: string;
  noDataText?: string;
  noResultsText?: string;
  compact?: boolean;
  singleSelect?: boolean;
  lazy?: boolean;
  verticalFilter?: boolean;
  expandable?: boolean;
}

export interface TableMeta {
  [key: string]: string | number;
}

export type DataTableLimit = 1 | 5 | 10 | 15 | 25 | 50 | 100;
